import { FC, ReactElement } from 'react'

import { Badge, ListGroupItem, MaterialIcon } from '@/components'
import { convertDateToDateString, convertDateToDay, convertDateToTime } from '@/helpers/DateTimeParser'
import { shouldShowEndTime } from '@/helpers/tools'
import { trans } from '@/helpers/trans'
import { ExchangeResource, UserResource } from '@/models'
import { SettingResource } from '@/models/Setting'
import { ShiftStatus } from '@/models/Shift'

const generateReplacementUserComments = (users: UserResource[], byMe: boolean): string | null => {
    if (! users.length) {
        return null
    }

    if (byMe && users.length > 1) {
        return trans('containers.exchanges.numberOfPeopleOfferedToTakeOverYourShift', { number: users.length })
    }

    if (byMe) {
        return trans('containers.exchanges.onePersonOfferedToTakeOverYourShift')
    }

    if (users.length > 1) {
        return trans('containers.exchanges.numberOfPeopleOfferedToTakeOverThisShift', { number: users.length })
    }

    return trans('containers.exchanges.onePersonOfferedToTakeOverThisShift')
}

const generateShiftTime = (exchange: ExchangeResource, showEndTime: boolean): string => {
    const ownerShiftPlanning = exchange.shift.shiftPlannings
        .find(shiftPlanning => shiftPlanning.user?.id === exchange.owner.id)

    if (ownerShiftPlanning && showEndTime) {
        return `${convertDateToTime(ownerShiftPlanning.start)} - ${convertDateToTime(ownerShiftPlanning.end)}`
    }

    if (showEndTime) {
        return `${convertDateToTime(exchange.shift.start)} - ${convertDateToTime(exchange.shift.end)}`
    }

    return convertDateToTime(ownerShiftPlanning?.start || exchange.shift.start)
}

interface ExchangeListItemProps {
    exchange: ExchangeResource
    byMe: boolean
    settings?: SettingResource[]
    onClick?: () => void
}

export const ExchangeListItem: FC<ExchangeListItemProps> = ({
    exchange,
    onClick = () => {},
    byMe,
    settings = [],
}): ReactElement => {
    const exchangeObject = new ShiftStatus(exchange.shift)
    const department = exchange.shift.department?.name ?? null
    const group = exchange.shift.department?.group?.name
    const comments = generateReplacementUserComments(exchange.replacementUsers, byMe)
    const showEndTime = shouldShowEndTime(settings, exchange.shift.department?.id)
    const shiftTime = generateShiftTime(exchange, showEndTime)
    const icon = exchangeObject.getIcon()

    return (
        <ListGroupItem onClick={onClick}>
            <div className="list-group-item__header text">
                <div className="d-flex flex-row">
                    <div className="font-weight-bold">{convertDateToDay(exchange.shift.start)}</div>
                    <div className="text text-nowrap ml-1"> {`- ${convertDateToDateString(exchange.shift.start)}`}</div>
                </div>
                <div>{shiftTime}</div>
                {comments && <small>{comments}</small>}
            </div>
            <div className="align-self-center d-flex flex-column">
                <Badge className="ml-auto" color={exchangeObject.getColor()}>
                    {exchangeObject.getLabel()}
                    {icon && (
                        <span className="ml-1">
                            <MaterialIcon fontSize={18} kind={icon} color="white" />
                        </span>
                    )}
                </Badge>
                <div className="small mt-1 text-right">
                    {`${group ? `${group} - ` : ''}${department}`}
                </div>
            </div>
        </ListGroupItem>
    )
}
